import { FileActionButton } from "../file-action/file-action-controls";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { useState, useMemo, useImperativeHandle, forwardRef } from "react";
import * as XLSX from "xlsx";


interface RawMaterialMovementLog {
  movementType: string;
  createdDate: string;
  rawMaterialName: string;
  quantity: number;
  vendorName: string;
  invoiceNumber: string;
  sourceType: string;
  sourceValue: string | null;
  documentDetails: string;
  note: string;
  createdByName?: string;
  phoneNumber?: string;
}


interface MovementLogTableProps {
  data: RawMaterialMovementLog[];
  loading: boolean;
  keyword: string;
}


export const RawMaterialMovementLogTable = forwardRef<{ resetFilters: () => void }, MovementLogTableProps>(
  ({ data, loading, keyword }, ref) => {
    // Filter state
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [rawMaterialFilter, setRawMaterialFilter] = useState<string[]>([]);
    const [vendorFilter, setVendorFilter] = useState<string[]>([]);
    const [sourceTypeFilter, setSourceTypeFilter] = useState<string[]>([]);
    const [sourceRefFilter, setSourceRefFilter] = useState<string[]>([]);


    // Sort state
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<1 | -1>(1);


    // Expose reset function to parent component
    useImperativeHandle(ref, () => ({
      resetFilters: () => {
        setTypeFilter([]);
        setRawMaterialFilter([]);
        setVendorFilter([]);
        setSourceTypeFilter([]);
        setSourceRefFilter([]);
        setSortField(null);
        setSortOrder(1);
      }
    }));


    // Sort data by date (newest first) as base
    const sortedData = useMemo(() => {
      if (!data || data.length === 0) return [];
      return [...data].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
    }, [data]);


    // Apply all filters to get filtered data
    const filteredData = useMemo(() => {
      let filtered = sortedData;


      // Apply keyword search across all columns
      if (keyword.trim()) {
        const searchTerm = keyword.toLowerCase();
        filtered = filtered.filter(item =>
          item.movementType?.toLowerCase().includes(searchTerm) ||
          item.rawMaterialName?.toLowerCase().includes(searchTerm) ||
          item.vendorName?.toLowerCase().includes(searchTerm) ||
          item.sourceType?.toLowerCase().includes(searchTerm) ||
          item.sourceValue?.toLowerCase().includes(searchTerm) ||
          item.documentDetails?.toLowerCase().includes(searchTerm) ||
          item.note?.toLowerCase().includes(searchTerm) ||
          item.createdByName?.toLowerCase().includes(searchTerm) ||
          item.phoneNumber?.toLowerCase().includes(searchTerm) ||
          item.invoiceNumber?.toLowerCase().includes(searchTerm) ||
          item.quantity?.toString().includes(searchTerm)
        );
      }


      if (typeFilter.length > 0) {
        filtered = filtered.filter(item => typeFilter.includes(item.movementType));
      }
      if (rawMaterialFilter.length > 0) {
        filtered = filtered.filter(item => rawMaterialFilter.includes(item.rawMaterialName));
      }
      if (vendorFilter.length > 0) {
        filtered = filtered.filter(item => vendorFilter.includes(item.vendorName));
      }
      if (sourceTypeFilter.length > 0) {
        filtered = filtered.filter(item => sourceTypeFilter.includes(item.sourceType));
      }
      if (sourceRefFilter.length > 0) {
        filtered = filtered.filter(item => item.sourceValue && sourceRefFilter.includes(item.sourceValue));
      }


      // Apply sorting if a sort field is selected
      if (sortField) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[sortField as keyof RawMaterialMovementLog];
          const bVal = b[sortField as keyof RawMaterialMovementLog];


          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;


          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortOrder * aVal.localeCompare(bVal);
          }


          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortOrder * (aVal - bVal);
          }


          return 0;
        });
      }


      return filtered;
    }, [sortedData, typeFilter, rawMaterialFilter, vendorFilter, sourceTypeFilter, sourceRefFilter, keyword, sortField, sortOrder]);


    // Get unique values for each filter column based on current filtered data
    const getUniqueValues = (field: keyof RawMaterialMovementLog, currentFilters: Partial<Record<keyof RawMaterialMovementLog, string[]>>): string[] => {
      if (!sortedData || sortedData.length === 0) return [];


      let baseData = sortedData;


      // Apply all other filters except the current one to get cascading effect
      if (currentFilters.movementType && field !== 'movementType') {
        baseData = baseData.filter(item => currentFilters.movementType!.includes(item.movementType));
      }
      if (currentFilters.rawMaterialName && field !== 'rawMaterialName') {
        baseData = baseData.filter(item => currentFilters.rawMaterialName!.includes(item.rawMaterialName));
      }
      if (currentFilters.vendorName && field !== 'vendorName') {
        baseData = baseData.filter(item => currentFilters.vendorName!.includes(item.vendorName));
      }
      if (currentFilters.sourceType && field !== 'sourceType') {
        baseData = baseData.filter(item => currentFilters.sourceType!.includes(item.sourceType));
      }
      if (currentFilters.sourceValue && field !== 'sourceValue') {
        baseData = baseData.filter(item => item.sourceValue && currentFilters.sourceValue!.includes(item.sourceValue));
      }


      const uniqueValues = Array.from(new Set(baseData.map(item => item[field]).filter(val => val !== null && val !== undefined))) as string[];
      return uniqueValues.sort();
    };


    // Current filter state for cascading
    const currentFilters = useMemo(() => ({
      movementType: typeFilter.length > 0 ? typeFilter : undefined,
      rawMaterialName: rawMaterialFilter.length > 0 ? rawMaterialFilter : undefined,
      vendorName: vendorFilter.length > 0 ? vendorFilter : undefined,
      sourceType: sourceTypeFilter.length > 0 ? sourceTypeFilter : undefined,
      sourceValue: sourceRefFilter.length > 0 ? sourceRefFilter : undefined,
    }), [typeFilter, rawMaterialFilter, vendorFilter, sourceTypeFilter, sourceRefFilter]);


    // Get available options for each filter
    const typeOptions = useMemo(() => getUniqueValues('movementType', currentFilters), [sortedData, currentFilters]);
    const rawMaterialOptions = useMemo(() => getUniqueValues('rawMaterialName', currentFilters), [sortedData, currentFilters]);
    const vendorOptions = useMemo(() => getUniqueValues('vendorName', currentFilters), [sortedData, currentFilters]);
    const sourceTypeOptions = useMemo(() => getUniqueValues('sourceType', currentFilters), [sortedData, currentFilters]);
    const sourceRefOptions = useMemo(() => getUniqueValues('sourceValue', currentFilters), [sortedData, currentFilters]);


    // Clear all filters
    const clearAllFilters = () => {
      setTypeFilter([]);
      setRawMaterialFilter([]);
      setVendorFilter([]);
      setSourceTypeFilter([]);
      setSourceRefFilter([]);
      setSortField(null);
      setSortOrder(1);
    };


    // Handle sorting
    const handleSort = (field: string) => {
      if (sortField === field) {
        setSortOrder(sortOrder === 1 ? -1 : 1);
      } else {
        setSortField(field);
        setSortOrder(1);
      }
    };


    // Check if any filters are active
    const hasActiveFilters = typeFilter.length > 0 || rawMaterialFilter.length > 0 ||
      vendorFilter.length > 0 || sourceTypeFilter.length > 0 || sourceRefFilter.length > 0 || keyword.trim() !== "";


    // Export function to convert data to Excel and download
    const exportToExcel = () => {
      if (filteredData.length === 0) {
        return;
      }

      try {
        const exportData = filteredData.map((item) => ({
          "Movement Type": item.movementType,
          "Date": new Date(item.createdDate).toLocaleDateString("en-GB"),
          "Raw Material Name": item.rawMaterialName,
          "Quantity": item.quantity,
          "Vendor Name": item.vendorName,
          "Invoice Number": item.invoiceNumber,
          "Source Type": item.sourceType,
          "Source / Reference": item.sourceValue || "-",
          "Document Details": item.documentDetails,
          "Note": item.note,
          "Created By": item.createdByName || "",
          "Phone Number": item.phoneNumber || "",
        }));

        // Create worksheet from JSON
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Format Quantity column as number with decimals
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        for (let row = 1; row <= range.e.r; row++) {
          const qtyCell = XLSX.utils.encode_cell({ r: row, c: 3 });
          if (worksheet[qtyCell]) {
            worksheet[qtyCell].t = "n";
            worksheet[qtyCell].z = "#,##0.00";
          }
        }

        // Set column widths for better readability
        worksheet["!cols"] = [
          { width: 15 }, // Movement Type
          { width: 12 }, // Date
          { width: 25 }, // Raw Material Name
          { width: 12 }, // Quantity
          { width: 20 }, // Vendor Name
          { width: 18 }, // Invoice Number
          { width: 15 }, // Source Type
          { width: 20 }, // Source / Reference
          { width: 25 }, // Document Details
          { width: 20 }, // Note
          { width: 20 }, // Created By
          { width: 16 }, // Phone Number
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Movement Log");

        const date = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `raw_material_movement_log_${date}.xlsx`);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
      }
    };


    // Custom cell for 'type' - colored text with background
    const typeBody = (rowData: RawMaterialMovementLog) => {
      let color: string;
      let backgroundColor: string;


      switch (rowData.movementType) {
        case "Inward":
          color = "#22c55e";
          backgroundColor = "#f0fdf4";
          break;
        case "Outward":
          color = "#3b82f6";
          backgroundColor = "#eff6ff";
          break;
        case "Adjust":
          color = "#f59e0b";
          backgroundColor = "#fffbeb";
          break;
        default:
          color = "#6b7280";
          backgroundColor = "#f9fafb";
      }


      return (
        <span style={{
          color: color,
          backgroundColor: backgroundColor,
          padding: '4px 12px',
          borderRadius: '6px',
          fontWeight: '500',
          fontSize: '0.875rem'
        }}>
          {rowData.movementType}
        </span>
      );
    };


    const dateBody = (rowData: RawMaterialMovementLog) => {
      const dt = new Date(rowData.createdDate);
      return dt.toLocaleDateString('en-GB');
    };


    // Filterable column header component
    const FilterableColumnHeader = ({
      title,
      filterValue,
      onFilterChange,
      options,
      sortField: columnField,
      showSort = true
    }: {
      title: string;
      filterValue: string[];
      onFilterChange: (value: string[]) => void;
      options: string[];
      sortField?: string;
      showSort?: boolean;
    }) => {
      const isCurrentlySorted = sortField === columnField;
      const activeFilterCount = filterValue.length;


      return (
        <div className="flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="font-semibold">{title}</span>


          <div className="flex align-items-center gap-1">
            {/* Filter dropdown */}
            <div className="p-overlay-badge">
              <MultiSelect
                value={filterValue}
                options={options}
                onChange={(e) => onFilterChange(e.value)}
                placeholder={`Filter ${title}`}
                maxSelectedLabels={0}
                selectedItemsLabel={`${activeFilterCount} selected`}
                className="custom-multiselect"
                style={{
                  width: '40px',
                  height: '32px',
                }}
                panelStyle={{ minWidth: '200px' }}
                display="chip"
                pt={{
                  root: {
                    style: {
                      border: activeFilterCount > 0 ? '2px solid #22c55e' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: activeFilterCount > 0 ? '#f0fdf4' : 'white',
                      transition: 'all 0.3s ease',
                    }
                  },
                  trigger: {
                    style: {
                      width: '100%',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  },
                  label: {
                    style: { display: 'none' }
                  }
                }}
              >
                <template>
                  <div className="flex align-items-center justify-content-center" style={{ width: '100%' }}>
                    <i
                      className="pi pi-filter"
                      style={{
                        fontSize: '0.9rem',
                        color: activeFilterCount > 0 ? '#22c55e' : '#6b7280'
                      }}
                    />
                    {activeFilterCount > 0 && (
                      <span
                        className="ml-1 badge-custom"
                        style={{
                          background: '#22c55e',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '0 5px',
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          minWidth: '16px',
                          textAlign: 'center'
                        }}
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                </template>
              </MultiSelect>
            </div>


            {/* Sort buttons */}
            {showSort && columnField && (
              <Button
                icon={isCurrentlySorted && sortOrder === -1 ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up"}
                className="p-button-text p-button-sm"
                onClick={() => handleSort(columnField)}
                style={{
                  color: isCurrentlySorted ? '#22c55e' : '#6b7280',
                  padding: '4px 8px',
                  minWidth: 'auto',
                  transition: 'all 0.3s ease'
                }}
                tooltip={`Sort ${isCurrentlySorted ? (sortOrder === 1 ? 'Descending' : 'Ascending') : 'Ascending'}`}
                tooltipOptions={{ position: 'top' }}
              />
            )}
          </div>
        </div>
      );
    };


    return (
      <div className="col-12">
        {/* Section Header */}
        <div className="flex align-items-center gap-2 mb-3 px-2">
          <div style={{
            backgroundColor: 'rgb(76, 175, 80)',
            borderRadius: '8px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="pi pi-list" style={{ color: 'white', fontSize: '1.2rem' }}></i>
          </div>
          <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '1.1rem' }}>Movement Records</span>
        </div>


        <div className="card p-2" style={{ borderRadius: '12px' }}>
          {/* Header Actions: Export and Clear Filters */}
          {(filteredData.length > 0 || hasActiveFilters) && (
            <div className="flex justify-content-end mb-2 gap-2">
              {/* Export Button */}
              {filteredData.length > 0 && (
                <FileActionButton action="export"
                  label="Export"
                  icon="pi pi-file-excel"
                  className="export-btn-summary-tooltip"
                  onClick={exportToExcel}
                  style={{
                    backgroundColor: '#22c55e',
                    border: 'none',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#16a34a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#22c55e';
                  }}
                  tooltip={`Export ${filteredData.length} record${filteredData.length !== 1 ? 's' : ''} to Excel`}
                  tooltipOptions={{ position: 'top' }}
                />
              )}


              {/* Clear All Filters Button */}
              {hasActiveFilters && (
                <Button
                  label="Clear All Filters"
                  icon="pi pi-filter-slash"
                  className="p-button-outlined p-button-sm"
                  onClick={clearAllFilters}
                  style={{
                    borderColor: '#22c55e',
                    color: '#22c55e',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22c55e';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#22c55e';
                  }}
                />
              )}
            </div>
          )}


          <DataTable
            value={filteredData}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            emptyMessage="No records found"
            stripedRows
            className="raw-material-movements-table"
          >
            <Column
              body={typeBody}
              header={
                <FilterableColumnHeader
                  title="Type"
                  filterValue={typeFilter}
                  onFilterChange={setTypeFilter}
                  options={typeOptions}
                  sortField="movementType"
                />
              }
              style={{ minWidth: '11rem' }}
            />


            <Column
              header="Date"
              body={dateBody}
              style={{ minWidth: '7.5rem' }}
            />


            <Column
              field="rawMaterialName"
              header={
                <FilterableColumnHeader
                  title="Raw Material"
                  filterValue={rawMaterialFilter}
                  onFilterChange={setRawMaterialFilter}
                  options={rawMaterialOptions}
                  sortField="rawMaterialName"
                />
              }
              style={{ minWidth: '14rem' }}
            />


            <Column
              field="quantity"
              header="Quantity"
              sortable
              style={{ minWidth: '7rem' }}
            />


            <Column
              field="vendorName"
              header={
                <FilterableColumnHeader
                  title="Vendor"
                  filterValue={vendorFilter}
                  onFilterChange={setVendorFilter}
                  options={vendorOptions}
                  sortField="vendorName"
                />
              }
              style={{ minWidth: '12rem' }}
            />


            <Column
              field="invoiceNumber"
              header="Invoice Number"
              sortable
              style={{ minWidth: '10rem' }}
            />


            <Column
              field="sourceType"
              header={
                <FilterableColumnHeader
                  title="Source Type"
                  filterValue={sourceTypeFilter}
                  onFilterChange={setSourceTypeFilter}
                  options={sourceTypeOptions}
                  sortField="sourceType"
                />
              }
              style={{ minWidth: '12.5rem' }}
            />


            <Column
              field="sourceValue"
              header={
                <FilterableColumnHeader
                  title="Source / Reference"
                  filterValue={sourceRefFilter}
                  onFilterChange={setSourceRefFilter}
                  options={sourceRefOptions}
                  sortField="sourceValue"
                />
              }
              style={{ minWidth: '14rem' }}
            />


            <Column
              field="documentDetails"
              header="Document Details"
              sortable
              style={{ minWidth: '11rem' }}
            />


            <Column
              field="note"
              header="Note"
              sortable
              style={{ minWidth: '9.5rem' }}
            />


            <Column
              field="createdByName"
              header="Created By"
              sortable
              style={{ minWidth: '150px' }}
            />


            <Column
              field="phoneNumber"
              header="Phone Number"
              sortable
              style={{ minWidth: '140px' }}
            />
          </DataTable>


          <style>{`
          .custom-multiselect .p-multiselect-trigger {
            padding: 0 !important;
          }
          
          .custom-multiselect .p-multiselect-label {
            display: none !important;
          }


          .raw-material-movements-table .p-datatable-thead > tr > th {
            background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
            border-bottom: 2px solid #e5e7eb;
            padding: 1rem 0.75rem;
          }


          .raw-material-movements-table .p-datatable-thead > tr > th:hover {
            background: linear-gradient(to bottom, #f0fdf4, #dcfce7);
            transition: background 0.3s ease;
          }


          .raw-material-movements-table .p-multiselect-panel .p-multiselect-header {
            background: #f0fdf4;
            border-bottom: 2px solid #22c55e;
          }


          .raw-material-movements-table .p-multiselect-panel .p-multiselect-item:hover {
            background: #dcfce7;
          }


          .raw-material-movements-table .p-multiselect-panel .p-multiselect-item.p-highlight {
            background: #22c55e;
            color: white;
          }


          .raw-material-movements-table .p-button:hover {
            transform: scale(1.1);
            transition: transform 0.2s ease;
          }
        `}</style>
        </div>
      </div>
    );
  }
);
RawMaterialMovementLogTable.displayName = 'RawMaterialMovementLogTable';
